import type { KarlPlanSnapshot } from "./types";
import type { CableSourceEdit } from "../CablesChatPanel/types";

export interface PlanSnapshotArgs {
  sourceEdit?: CableSourceEdit;
}

export type PlanSnapshot = (args: PlanSnapshotArgs) => KarlPlanSnapshot;

export type IsSamePlanSnapshot = (
  a: KarlPlanSnapshot,
  b: KarlPlanSnapshot,
) => boolean;

const emptyRecord: Record<string, never> = {};
const NO_ZONES: Array<{ sourceLabel: string }> = [];

export const planSnapshot: PlanSnapshot = ({ sourceEdit }) => ({
  // Live references, not clones: `askArmSettled` compares per key.
  placed: sourceEdit?.placed ?? emptyRecord,
  added: sourceEdit?.added ?? emptyRecord,
  removed: sourceEdit?.removed ?? emptyRecord,
  grabbedLabel: sourceEdit?.grabbedLabel ?? null,
  addingName: sourceEdit?.addingName ?? null,
  isSelectingDevices: sourceEdit?.isSelectingDevices ?? false,
  // The selection object is large and gets a new identity every render, so only
  // its presence is carried: comparing it would make every render a change.
  hasDeviceSelection: (sourceEdit?.deviceSelection ?? null) !== null,
  selectedCircuit: sourceEdit?.selectedCircuit ?? null,
  zones: sourceEdit?.zones ?? NO_ZONES,
  drawingZoneFor: sourceEdit?.drawingZoneFor ?? null,
  mainBoardLabel: sourceEdit?.mainBoardLabel ?? null,
  pickingMainBoard: sourceEdit?.pickingMainBoard ?? false,
  pickingFeederFor: sourceEdit?.pickingFeederFor ?? null,
  pickingUpstreamFor: sourceEdit?.feeders?.pickingUpstreamFor ?? null,
  feeders: sourceEdit?.feeders?.byLabel,
  boardFeeds: sourceEdit?.storedBoardFeeds ?? null,
});

export const isSamePlanSnapshot: IsSamePlanSnapshot = (a, b) =>
  // The edit hook replaces these records on every real change, so a reference
  // match means nothing in them moved.
  a.placed === b.placed &&
  a.added === b.added &&
  a.removed === b.removed &&
  a.grabbedLabel === b.grabbedLabel &&
  a.addingName === b.addingName &&
  a.isSelectingDevices === b.isSelectingDevices &&
  a.hasDeviceSelection === b.hasDeviceSelection &&
  a.selectedCircuit === b.selectedCircuit &&
  a.zones === b.zones &&
  a.drawingZoneFor === b.drawingZoneFor &&
  a.mainBoardLabel === b.mainBoardLabel &&
  a.pickingMainBoard === b.pickingMainBoard &&
  a.pickingFeederFor === b.pickingFeederFor &&
  a.pickingUpstreamFor === b.pickingUpstreamFor &&
  a.feeders === b.feeders &&
  a.boardFeeds === b.boardFeeds;
